import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../hooks/use-toast';
import { orgApi } from '../phase567Api';
import { useAuth } from '../AuthContext';

const FAMILY_LABELS = {
  deal_links: 'Deal borrower links',
  loan_share_tokens: 'Loan status links',
  re_share_tokens: 'Transaction status links',
  upload_requests: 'Document upload links',
  esign_requests: 'E-sign links',
  fee_worksheet_approvals: 'Fee worksheet links',
  attorney_reviews: 'Attorney review links',
  lead_invites: 'Borrower / realtor invites',
  staff_invitations: 'Staff invitations',
  public_pdfs: 'Public PDF links',
  partner_tokens: 'Partner tokens',
  api_keys: 'API keys',
};

function errDescription(e) {
  return e?.response?.data?.detail?.message || e?.response?.data?.detail || String(e);
}

function isPermissionDenied(e) {
  return e?.response?.data?.detail?.code === 'permission_denied';
}

export default function IncidentTab() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canKillSessions = perms.includes('incident.kill_sessions');
  const canKillLinks = perms.includes('incident.kill_links');

  const [sessionsBusy, setSessionsBusy] = useState(false);
  const [sessionsConfirmOpen, setSessionsConfirmOpen] = useState(false);

  const [includeApiKeys, setIncludeApiKeys] = useState(false);
  const [linksBusy, setLinksBusy] = useState(false);
  const [linksConfirmOpen, setLinksConfirmOpen] = useState(false);
  const [linksResult, setLinksResult] = useState(null);

  const doKillSessions = async () => {
    setSessionsBusy(true);
    try {
      const d = await orgApi.killSessions();
      toast({ title: 'Signed out', description: `${d.affected} member(s) signed out everywhere.` });
    } catch (e) {
      if (!isPermissionDenied(e)) toast({ title: 'Failed', description: errDescription(e) });
    } finally {
      setSessionsBusy(false);
      setSessionsConfirmOpen(false);
    }
  };

  const doKillLinks = async () => {
    setLinksBusy(true);
    try {
      const d = await orgApi.killLinks(includeApiKeys);
      toast({ title: 'Links revoked', description: `${d.affected} link(s) revoked.` });
      setLinksResult(d.families || {});
    } catch (e) {
      if (!isPermissionDenied(e)) toast({ title: 'Failed', description: errDescription(e) });
    } finally {
      setLinksBusy(false);
      setLinksConfirmOpen(false);
    }
  };

  if (!canKillSessions && !canKillLinks) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center text-slate-400 text-sm" data-testid="incident-no-access">
        You don't have incident-response permissions in this workspace.
      </div>
    );
  }

  const familyRows = linksResult
    ? Object.entries(linksResult).sort((a, b) => (b[1] > 0) - (a[1] > 0))
    : [];

  return (
    <div className="space-y-6" data-testid="incident-tab">
      <p className="text-sm text-slate-500">
        Incident response. Both actions are immediate, audited, and cannot be undone. The Railway-side JWT_SECRET rotation
        in the runbook remains the cross-workspace nuclear option.
      </p>

      {canKillSessions && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Sign everyone out</div>
          <p className="text-sm text-slate-500 mt-2">
            Signs every member of this workspace out of every device. You keep your current session; to sign yourself out
            everywhere use your own row on the Team tab. Super-admins are not affected.
          </p>
          <button
            onClick={() => setSessionsConfirmOpen(true)}
            disabled={sessionsBusy}
            data-testid="incident-kill-sessions-btn"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ background: '#DC2626' }}
          >
            {sessionsBusy && <Loader2 size={14} className="animate-spin" />}
            Sign everyone out
          </button>

          <AlertDialog open={sessionsConfirmOpen} onOpenChange={setSessionsConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Sign everyone out?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every member of this workspace will be signed out of every device except you and any super-admins. This
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="incident-kill-sessions-confirm" onClick={doKillSessions}>
                  Sign everyone out
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {canKillLinks && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Revoke all shared links</div>
          <p className="text-sm text-slate-500 mt-2">
            Revokes every outstanding shared link in this workspace: borrower deal links, loan and transaction status
            links, upload, e-sign, fee worksheet, attorney review, invite, staff invitation, public PDF and partner
            links. Staff regenerate links per deal afterwards.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <Checkbox
              checked={includeApiKeys}
              onCheckedChange={(v) => setIncludeApiKeys(!!v)}
              data-testid="incident-include-api-keys"
              id="incident-include-api-keys"
            />
            <label htmlFor="incident-include-api-keys" className="text-sm text-slate-600">Also revoke all API keys</label>
          </div>
          <button
            onClick={() => setLinksConfirmOpen(true)}
            disabled={linksBusy}
            data-testid="incident-kill-links-btn"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ background: '#DC2626' }}
          >
            {linksBusy && <Loader2 size={14} className="animate-spin" />}
            Revoke all links
          </button>

          <AlertDialog open={linksConfirmOpen} onOpenChange={setLinksConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revoke all links?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every outstanding shared link in this workspace will stop working immediately{includeApiKeys ? ', including all API keys' : ' (API keys are not included)'}.
                  This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="incident-kill-links-confirm" onClick={doKillLinks}>
                  Revoke all links
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {linksResult && (
            <div className="mt-4 border border-slate-100 rounded-xl overflow-hidden" data-testid="incident-kill-links-result">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-2">Link type</th>
                    <th className="px-4 py-2 text-right">Revoked</th>
                  </tr>
                </thead>
                <tbody>
                  {familyRows.map(([key, count]) => (
                    <tr key={key} className="border-b border-slate-50">
                      <td className="px-4 py-2">{FAMILY_LABELS[key] || key}</td>
                      <td className="px-4 py-2 text-right">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
